import { useEffect, useState, useCallback, useMemo } from "react";
import { Col, Row, Button, Image, Form } from "react-bootstrap";
import { defineMessages, injectIntl } from "react-intl";
import PropTypes from "prop-types";
import { paymentService } from "../services/payment";
import { formatMoney, validate } from "../services/helpers";
import useI18n from "../hooks/useI18n";
import { useLocation } from "react-router-dom";

import Wizard from "./Wizard";
import usePaymentProviders from "../hooks/usePaymentProviders";
import isEmpty from "lodash.isempty";
import AlertDialog from "./AlertDialog";
import useUserAccount from "../hooks/useUserAccount";


const messages = defineMessages({
    choosePaymentMethod: {
        id: "Valitse maksutapa",
        defaultMessage: "Valitse maksutapa"
    },
    actionClose: {
        id: "Sulje",
        defaultMessage: "Sulje"
    },
    actionCancel: {
        id: "Peruuta",
        defaultMessage: "Peruuta"
    },
    errorOccurred: {
        id: "Tapahtui virhe",
        defaultMessage: "Tapahtui virhe",
    },
    somethingWentWrongTryAgain: {
        id: "Jotain meni vikaan, ole hyvä ja yritä uudelleen.",
        defaultMessage: "Jotain meni vikaan, ole hyvä ja yritä uudelleen.",
    },
    backToInvoice: {
        id: "Palaa laskun tietoihin",
        defaultMessage: "Palaa laskun tietoihin",
    },
    authError: {
        id: "Tunnistautuminen epäonnistui. Ole hyvä ja yritä uudelleen.",
        defaultMessage: "Tunnistautuminen epäonnistui. Ole hyvä ja yritä uudelleen."
    },
    onlineBanks: {
        id: "Verkkopankit",
        defaultMessage: "Verkkopankit"
    },
    goBack: {
        id: "Palaa takaisin",
        defaultMessage: "Palaa takaisin"
    },
    chooseWhatToPay: {
        id: "Valitse maksettava summa",
        defaultMessage: "Valitse maksettava summa"
    },
    payNextInstallment: {
        id: "seuraava erä",
        defaultMessage: "seuraava erä"
    },
    payFullAmount: {
        id: "koko summa",
        defaultMessage: "koko summa"
    },
    choosePaymentAmount: {
        id: "Valitse ensin maksettava summa.",
        defaultMessage: "Valitse ensin maksettava summa."
    },
    phoneNumber: {
        id: "Puhelinnumero",
        defaultMessage: "Puhelinnumero"
    },
    pay: {
        id: "Maksa",
        defaultMessage: "Maksa"
    },
    invalidPhoneNumber: {
        id: "Puhelinnumero on virheellinen",
        defaultMessage: "Puhelinnumero on virheellinen"
    },
    companyAccountNotSupported: {
        id: "Maksupalvelu ei välttämättä tue maksamista yritystileiltä. Jos maksaminen ei onnistu, voit maksaa laskun verkkopankissa käyttäen virtuaaliviivakoodia",
        defaultMessage: "Maksupalvelu ei välttämättä tue maksamista yritystileiltä. Jos maksaminen ei onnistu, voit maksaa laskun verkkopankissa käyttäen virtuaaliviivakoodia"
    }
});

export { messages };


const PaymentModal = (props) => {
    // translations
    const lng = props.intl.formatMessage;
    const location = useLocation();
    const [banks, setBanks] = useState([]);
    const [tinkBanks, setTinkBanks] = useState([]);
    const [error, setError] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [installmentToPay, setInstallmentToPay] = useState('');
    const [payplanpaymentstep, setPayplanpaymentstep] = useState(-1);
    const [selectedBankId, setSelectedBankId] = useState(null);
    const [phoneNumber, setPhoneNumber] = useState('');
    const nextPayplanInstallment = props.job?.payplan?.find(step => !step.ispaid) ?? null;
    const jobHasPayPlan = (props.jobstatus === 41);
    const { addNetaxeptPaymentMethods, addWorldlinePaymentMethods, checkForRestrictions, fetchTinkPaymentProviders, rearrangeArrayForSwish, getWorldlinePaymentMethods } = usePaymentProviders();
    const { getLocale, getCountry } = useI18n();
    const userLocale = getLocale();
    const { userIsCompany } = useUserAccount();

    const onlineBanks = {
        name: lng(messages.onlineBanks),
        bank_id: '',
        logo: "/img/building-columns-light.svg",
        rank: 0,
        style: 'tinkstyle'
    };

    const PHONE_NUMBER_NEEDED_FOR_METHOD = ['Vipps'];


    /**
     * Get payment methods from Tink, and create an array of payment methods to display in the modal.
     * 
     * Additional banks from Netaxept can be added with bankids via env. variable REACT_APP_NETAXEPT_PRESELECT_BANKS
     * Additional banks from Worldline can be added with bankids via env. variable REACT_APP_WORLDLINE_PRESELECT_BANKS
     * Tink banks can only be excluded from Tink console.
     */
    const getTinkPaymentMethods = useCallback(() => {

        fetchTinkPaymentProviders().then(providers => {
            // add 'Online banks' option for redirection to Tink banks if banks are not returned for some reason, but payment provider is Tink
            let tinkModalBanks = providers.length
                ? providers.map(bank => ({ ...bank, style: 'tinkstyle' })) // add different styling for logos from CDN
                : [onlineBanks];


            // add to results Netaxept payment methods if there are any
            tinkModalBanks = tinkModalBanks.concat(addNetaxeptPaymentMethods(props.includedNetsBanks));

            // add Worldline payment methods if there are any
            tinkModalBanks = tinkModalBanks.concat(addWorldlinePaymentMethods(props.includedWorldlineBanks));

            // sort banks alphabetically and set banks
            tinkModalBanks.sort((a, b) => a.name.localeCompare(b.name));

            // check if Swish is included in the payment methods, and rearrange it to be first if found
            tinkModalBanks = rearrangeArrayForSwish(tinkModalBanks);

            setTinkBanks(tinkModalBanks);
        }).catch(error => {
            setError(true);
            // show error message to user
            props.handleMakePaymentModal();
            props.handleAlertDialog(lng(messages.somethingWentWrongTryAgain), 'danger');
        });


    }, [props.includedNetsBanks, userLocale, lng, location]);


    // get Tink payment methods when initialized
    useEffect(() => {
        // no need to get Tink banks if user is redirected to Tink for payment method selection
        if (!props.redirectToTinkForPayment) {
            getTinkPaymentMethods();
        }
    }, [props.redirectToTinkForPayment]);


    useEffect(() => {
        if (props.show) {
            setShowAlert(false);
            setPayplanpaymentstep(0);
            let bankOptions = [];

            if (error && props.serviceProvider === paymentService.PAYMENTMETHODS.TINK) {
                // in error cases close modal, but if other service providers are available in the environment, allow paying with them
                setPayplanpaymentstep(-1);
                props.handleMakePaymentModal();
                // show error message to user
                props.handleAlertDialog(lng(messages.somethingWentWrongTryAgain), 'danger');
                return;
            }

            if (props.serviceProvider === paymentService.PAYMENTMETHODS.NETAXEPT) { // only use Netaxept payment methods
                // add banks from Netaxept passed in props to bank selection modal
                bankOptions = addNetaxeptPaymentMethods(props.includedNetsBanks);
                // check for any payment restrictions
                bankOptions = checkForRestrictions(bankOptions, props.mainstatus);

                setBanks(bankOptions);
            } else if (props.serviceProvider === paymentService.PAYMENTMETHODS.TINK && props.redirectToTinkForPayment) { // check for possible Netaxept and Worldline banks and use generic redirect button for Tink
                // add defined banks from Netaxept to bank selection modal if any
                bankOptions = addNetaxeptPaymentMethods(props.includedNetsBanks);
                // add banks from Worldline to bank selection modal if any
                bankOptions = bankOptions.concat(addWorldlinePaymentMethods(props.includedWorldlineBanks));
                // check for any payment restrictions
                bankOptions = checkForRestrictions(bankOptions, props.mainstatus);

                if (bankOptions.length === 0) {
                    // no other banks available after restriction checks, skip selection in MyRopo and redirect to Tink for bank selection
                    props.handleChoosePaymentMethod();
                }

                // add 'Online banks' option that redirects to Tink for bank selection
                bankOptions.push(onlineBanks);
                setBanks(bankOptions);
            } else if (props.serviceProvider === paymentService.PAYMENTMETHODS.TINK) { // check for possible Netaxept and Worldline banks in addition to Tink payment methods
                // check for any payment restrictions (tinkBanks are fetchd when the component is initialized)
                bankOptions = checkForRestrictions(tinkBanks, props.mainstatus);

                // show payment methods from Tink 
                setBanks(bankOptions);
            } else if (props.serviceProvider === paymentService.PAYMENTMETHODS.WORLDLINE) { // use only Worldline payment methods
                // add all allowed banks from Worldline and check for any restrictions
                bankOptions = getWorldlinePaymentMethods();
                bankOptions = checkForRestrictions(bankOptions, props.mainstatus);

                // show payment methods from Worldline
                setBanks(bankOptions);
            } else {
                // bank selection not supported, skip in MyRopo and redirect straight to payment provider to choose the bank
                props.handleChoosePaymentMethod();
            }

        }

    }, [props.show]);


    /**
     * Close the payment modal and ensure that payplan related settings are reset.
     *  installmentToPay is reset, and steps are set to -1.
     * Also reset selected bank to hide input field for phone and clear phone number.
     */
    const handleWizardClose = () => {
        setPayplanpaymentstep(-1);
        setShowAlert(false);
        setInstallmentToPay('');
        setSelectedBankId(null);
        setPhoneNumber('');
        props.handleMakePaymentModal();
    }

    /**
     * Handle moving backwards in the payment modal or close the modal completely,
     * if user is already on the first step.
     */
    const handleWizardBack = () => {
        setInstallmentToPay('');
        setPayplanpaymentstep(payplanpaymentstep - 1);

        if (payplanpaymentstep === 0) {
            // also close modal, if it is the first step
            props.handleMakePaymentModal();
        }
    }

    /**
     * Handle moving forward in the payment modal.
     * If no installment is chosen, show an error and do not allow to the next step.
     */
    const handleWizardNext = () => {
        if (isEmpty(installmentToPay)) {
            // installment not chosen, show error in modal
            setShowAlert(true);
        } else {
            // move to next step
            continueToBankSelection(installmentToPay);
        }
    }

    /**
     * Handle continuing to bank selection step, and pass the chosen amount to pay.
     * For jobs with a payment plan, the chosen amount can be the next installment or the full amount.
     * 
     * If environment does not support bank selection in MyRopo, user is redirected to the payment provider to choose the bank.
     * 
     * @param {*} installmentAmount 
     */
    const continueToBankSelection = (installmentAmount) => {
        setInstallmentToPay(installmentAmount);
        setShowAlert(false);

        if (jobHasPayPlan && !props.preselectPayment) {
            // bank selection in MyRopo is not supported, redirect to payment provider to continue the payment
            props.handleChoosePaymentMethod('', installmentAmount);
        } else {
            // if no payplan, or preselectpayment is true, continue to next step
            setPayplanpaymentstep(payplanpaymentstep + 1);
        }
    }

    /**
     * Handle button click action for payment method selection.
     * If e.g. Vipps is selected, show phone number input field.
     * Otherwise continue with the payment process.
     * 
     * @param {*} bank_id 
     * @param {*} installmentToPay 
     */
    const bankButtonOnClickAction = (bank_id, installmentToPay) => {
        // check if phone input should be shown
        if (PHONE_NUMBER_NEEDED_FOR_METHOD.includes(bank_id)) {
            // hide all other banks
            setSelectedBankId(bank_id);
        } else {
            setSelectedBankId(null);
            props.handleChoosePaymentMethod(bank_id, installmentToPay);
        }
    }

    /**
     * Helper to render a buttons for the modal and its steps.
     * Variant and classes are by default same for all buttons
     * 
     * @param {*} value button id and value
     * @param {*} passedFunction onClick function to call
     * @param {*} label button label
     * @param {*} image optional image to display in the button, before the label 
     * @param {*} variant "secondary" by default
     * @param {*} buttonClasses "payment-method" by default
     * @returns 
     */
    const renderButton = (value, passedFunction, label, image, variant = "secondary", buttonClasses = "payment-method") => (
        <Button
            id={value}
            value={value}
            onClick={passedFunction}
            variant={variant}
            className={buttonClasses}
        >
            {image}
            {label}
        </Button>
    )

    /**
     * Helper to render phone input field for (Vipps) payments.
     * @returns 
     */
    const renderPhoneInput = () => {
        /**
         * Handle form submission for phone number input.
         * If the phone number is valid, proceed with the payment.
         * @param {*} e 
         */
        const handleSubmit = (e) => {
            e.preventDefault();

            // move along in the payments  process, if phone number meets the criteria
            if (validate(e.currentTarget)) {
                props.handleChoosePaymentMethod(selectedBankId, installmentToPay, phoneNumber);
            }
        };

        return (
            <Form id="payment-phone-number" className="mt-2" aria-label={lng(messages.phoneNumber)} onSubmit={handleSubmit} noValidate>
                <Form.Floating className="mb-3">
                    <Form.Control
                        id="payment-phone"
                        type="tel"
                        placeholder={lng(messages.phoneNumber)}
                        value={phoneNumber}
                        onChange={e => setPhoneNumber(e.target.value)}
                        pattern="^\+?\d{6,20}$" // 6-20 digits, allow leading + sign
                        validationmessage={lng(messages.invalidPhoneNumber)}
                        aria-required="true"
                        required
                    />
                    <Form.Label htmlFor="payment-phone">{lng(messages.phoneNumber)}</Form.Label>
                    <Form.Control.Feedback type="invalid" id="phone-error" />
                </Form.Floating>
                <Button
                    variant="primary"
                    className="mt-2"
                    type="submit"
                >
                    {lng(messages.pay)}
                </Button>
            </Form>
        )
    }

    /**
     * create wizard steps based on the job
     */
    const wizardSteps = useMemo(() => {
        const steps = [];
        // bank selection as default step whenever bank selection is allowed in MyRopo
        if (props.preselectPayment) {
            steps.push({
                title: lng(messages.choosePaymentMethod),
                returnText: jobHasPayPlan ? lng(messages.goBack) : lng(messages.actionClose),
                bodyContent: (
                    <>
                        {userIsCompany() && props.serviceProvider === paymentService.PAYMENTMETHODS.TINK && getCountry() === 'fi' && (
                            <AlertDialog
                                type="primary"
                                message={lng(messages.companyAccountNotSupported)}
                            />
                        )}
                        <Row className="d-grid mb-2" aria-live='polite'>
                            {banks.length > 0 && (
                                PHONE_NUMBER_NEEDED_FOR_METHOD.includes(selectedBankId)
                                    ? banks.filter(row => row.bank_id === selectedBankId).map((row) => (
                                        <Col xs={{ order: row.rank }} key={row.bank_id} className={"pb-3 " + row.rank}>
                                            {renderButton(
                                                row.name,
                                                () => setSelectedBankId(null), // resetting the selected hideds input and shows all banks
                                                row.name,
                                                <Image id={row.name} alt={row.name} src={row.logo} aria-hidden="true" />
                                            )}
                                            {renderPhoneInput()}
                                        </Col>
                                    ))
                                    : banks.map((row) => (
                                        <Col xs={{ order: row.rank }} key={row.bank_id} className={"pb-3 " + row.rank}>
                                            {renderButton(
                                                row.name,
                                                () => bankButtonOnClickAction(row.bank_id, installmentToPay),
                                                row.name,
                                                <Image id={row.name} alt={row.name} src={row.logo} aria-hidden="true" />
                                            )}
                                        </Col>
                                    ))
                            )}
                        </Row>
                    </>

                ),
                closeFunction: () => handleWizardClose(),
                backFunction: () => handleWizardBack()
            })
        }

        if (jobHasPayPlan) {
            // if a payment plan exists for the job, the first step is to choose how much to pay
            steps.unshift({
                title: lng(messages.chooseWhatToPay),
                returnText: lng(messages.actionClose),
                bodyContent: (
                    <>
                        {showAlert && (
                            <AlertDialog
                                message={lng(messages.choosePaymentAmount)}
                                type="danger"
                            />
                        )}
                        <div className="d-grid gap-3 mb-2">
                            {renderButton(
                                'next',
                                () => continueToBankSelection('next'),
                                `${formatMoney(nextPayplanInstallment?.amount, userLocale, props.job.currency)} - ${lng(messages.payNextInstallment)}`,
                            )}
                            {renderButton(
                                'full',
                                () => continueToBankSelection('full'),
                                `${formatMoney(props.job.openamount, userLocale, props.job.currency)} - ${lng(messages.payFullAmount)}`,
                            )}
                        </div>
                    </>

                ),
                closeFunction: () => handleWizardClose(),
                nextFunction: () => handleWizardNext()
            });
        }

        return steps;
    }, [banks, installmentToPay, props.job, jobHasPayPlan, lng, nextPayplanInstallment, userLocale, payplanpaymentstep, showAlert, selectedBankId, phoneNumber])


    if (banks.length < 2 && props.redirectToTinkForPayment) {
        return null;
    }

    return (
        <Wizard
            currentStep={payplanpaymentstep}
            steps={wizardSteps}
        />
    );
};

PaymentModal.propTypes = {
    'intl': PropTypes.object.isRequired,
    'show': PropTypes.bool,
    'serviceProvider': PropTypes.string,
    'handleMakePaymentModal': PropTypes.func,
    'handleChoosePaymentMethod': PropTypes.func,
    'handleAlertDialog': PropTypes.func,
    'includedNetsBanks': PropTypes.array,
    'includedWorldlineBanks': PropTypes.array,
    'redirectToTinkForPayment': PropTypes.bool,
    'mainstatus': PropTypes.number,
    'jobstatus': PropTypes.number,
    'job': PropTypes.object,
    'preselectPayment': PropTypes.bool
};

export default injectIntl(PaymentModal);
